/**
 * CSR Inbox — Demo Flow 2.
 *
 * Polls /api/handoffs every 10s. Left rail lists open handoffs
 * (sorted by priority then age). Right pane shows full member context:
 * name, language flag, intent label, original Spanish text + English
 * translation, and Take Ownership / Mark Resolved actions.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { authFetch, authFetchJson } from '../../lib/authFetch';
import { SimulateInboundSMSButton } from './EngagementDemoFlowComponents';
import { useTabbedChrome } from '@/components/weavein';

interface HandoffSummary {
  id: string;
  member_id: number;
  intent_label: string | null;
  intent_confidence: number | null;
  detected_language: string | null;
  priority: 'urgent' | 'high' | 'normal' | 'low';
  status: 'open' | 'assigned' | 'resolved' | 'closed';
  context_summary: string | null;
  last_message_text: string | null;
  last_message_at: string | null;
  created_at: string | null;
  member_first_name: string | null;
  member_last_name: string | null;
  member_preferred_language: string | null;
  assigned_to_user_id: number | null;
}

interface HandoffDetail extends HandoffSummary {
  chatflow_session_id: string | null;
  intent_classification_id: string | null;
  member: {
    id: number;
    first_name: string | null;
    last_name: string | null;
    preferred_language: string | null;
    phone: string | null;
  } | null;
  conversation_history: Array<{ role?: string; content?: string; ts?: string }>;
  original_text: string | null;
  english_text: string | null;
}

const REFERRABLE_INTENTS = new Set(['transportation', 'housing', 'appointment', 'complaint']);

const CreateReferralFromIntentButton: React.FC<{
  cboId: number;
  classificationId: string | null;
  intentLabel: string | null;
}> = ({ cboId, classificationId, intentLabel }) => {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; referralId?: number; error?: string } | null>(null);
  if (!classificationId || !intentLabel || !REFERRABLE_INTENTS.has(intentLabel)) return null;
  const onClick = async () => {
    setBusy(true);
    try {
      const r = await authFetch(
        `/api/engagement-intelligence/orgs/${cboId}/intents/${classificationId}/create-referral`,
        { method: 'POST' }
      );
      if (r.ok) {
        const j = await r.json();
        setResult({ ok: true, referralId: j.service_referral_id });
      } else {
        setResult({ ok: false, error: `HTTP ${r.status}` });
      }
    } catch (e: any) {
      setResult({ ok: false, error: e?.message || 'failed' });
    } finally {
      setBusy(false);
    }
  };
  if (result?.ok && result.referralId) {
    return (
      <span className="rounded-full bg-emerald-600 px-2 py-0.5 text-[10px] font-semibold uppercase text-white">
        ✓ Referral #{result.referralId} created
      </span>
    );
  }
  return (
    <button
      onClick={onClick}
      disabled={busy}
      className="rounded-full border border-indigo-300 bg-white px-2 py-0.5 text-[10px] font-semibold uppercase text-indigo-700 hover:bg-indigo-50 disabled:opacity-50"
      title="Convert this member-originating intent into a service referral"
    >
      {busy ? 'Creating…' : '↳ Create referral'}
    </button>
  );
};

const LANG_FLAG: Record<string, string> = {
  en: '🇺🇸', es: '🇲🇽', vi: '🇻🇳', zh: '🇨🇳', ar: '🇸🇦', ko: '🇰🇷',
};

function priorityColor(p: HandoffSummary['priority']): string {
  switch (p) {
    case 'urgent': return 'bg-red-600';
    case 'high':   return 'bg-orange-500';
    case 'normal': return 'bg-blue-500';
    default:       return 'bg-gray-400';
  }
}

interface Props {
  organizationId: number;
  currentUserId: number;
  /** When set, the handoff list is filtered to this member and the
      banner shows that we're scoped. Passed in by V2CommsHub when the
      user clicks "Open CSR handoff" from the inbox conversation
      detail. */
  prefilterMemberId?: number | null;
  /** Called when the user clears the pre-filter or clicks an
      "Open conversation" button on a handoff card. */
  onOpenConversation?: (memberId: number, channel?: string) => void;
}

// Un-replied inbound with no handoff (from GET /api/handoffs/unrouted)
interface UnroutedItem {
  kind: 'unrouted';
  conversation_id: string;
  member_id: number;
  member_first_name: string | null;
  member_last_name: string | null;
  member_preferred_language?: string | null;
  channel: string;
  last_message_text: string | null;
  last_message_at: string | null;
  unread_count: number;
  intent_label: string | null;
  detected_language: string | null;
}

interface MemberOption { id: number; first_name: string | null; last_name: string | null; preferred_language?: string | null; language?: string | null; }
interface IntentTaxonomyOption {
  id: number; label: string; description: string | null;
  auto_handoff: boolean; default_priority: string; example_phrases?: string[];
}

interface RecentEvent {
  event_type: string;
  channel: string | null;
  language: string | null;
  occurred_at: string | null;
  campaign_name: string | null;
}
interface ConsentRow {
  category_key: string;
  granted: boolean;
  granted_at: string | null;
  method: string | null;
}
interface SuggestedAction {
  action_type: 'crm_ticket' | 'uuhp_ticket' | 'weavein_ticket' | 'send_resources';
  label: string;
  description: string;
  icon: string;
  system: string;
  primary?: boolean;
  preview_message?: string;
  preview_language?: string;
}
interface HandoffContextResponse {
  handoff: HandoffDetail;
  member: any;
  recent_events: RecentEvent[];
  consents: ConsentRow[];
  suggested_actions: SuggestedAction[];
}

export const CSRInboxPage: React.FC<Props> = ({ organizationId, currentUserId, prefilterMemberId, onOpenConversation }) => {
  const tabbed = useTabbedChrome();
  const [handoffs, setHandoffs] = useState<HandoffSummary[]>([]);
  // Un-replied inbound that produced no handoff — the safety-net triage lane.
  const [unrouted, setUnrouted] = useState<UnroutedItem[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [detail, setDetail] = useState<HandoffDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({
    member_id: '', intent_label: '', priority: 'high' as 'urgent'|'high'|'normal'|'low',
    detected_language: 'en', last_message_text: '', context_summary: '',
  });
  const [creating, setCreating] = useState(false);
  const [memberOptions, setMemberOptions] = useState<MemberOption[]>([]);
  const [intentOptions, setIntentOptions] = useState<IntentTaxonomyOption[]>([]);

  // Fetch member + intent dropdown options once when the modal first opens.
  useEffect(() => {
    if (!showCreate) return;
    if (memberOptions.length === 0) {
      authFetchJson<{ members: MemberOption[] } | MemberOption[]>(
        `/api/members?page=1&page_size=500`,
      )
        .then(d => {
          const list = Array.isArray(d) ? d : (d as { members: MemberOption[] }).members || [];
          setMemberOptions(list);
        })
        .catch(() => setMemberOptions([]));
    }
    if (intentOptions.length === 0) {
      authFetchJson<IntentTaxonomyOption[]>(
        `/api/intent-taxonomy?organization_id=${organizationId}`,
      )
        .then(d => {
          setIntentOptions(d || []);
          // Default the form to the first intent label so the dropdown isn't empty.
          if (d && d.length > 0 && !createForm.intent_label) {
            setCreateForm(f => ({ ...f, intent_label: d[0].label, priority: (d[0].default_priority as any) || f.priority }));
          }
        })
        .catch(() => setIntentOptions([]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCreate, organizationId]);

  const selectedIntent = intentOptions.find(i => i.label === createForm.intent_label);

  const refreshList = useCallback(async () => {
    try {
      const [data, unroutedData] = await Promise.all([
        authFetchJson<HandoffSummary[]>(`/api/handoffs?organization_id=${organizationId}`),
        authFetchJson<UnroutedItem[]>(`/api/handoffs/unrouted?organization_id=${organizationId}`).catch(() => []),
      ]);
      setHandoffs(data);
      setUnrouted(Array.isArray(unroutedData) ? unroutedData : []);
      if (!selected && data.length > 0) setSelected(data[0].id);
    } catch (e) {
      console.warn('Failed to fetch handoffs', e);
    }
  }, [organizationId, selected]);

  useEffect(() => {
    refreshList();
    const id = setInterval(refreshList, 10_000);
    return () => clearInterval(id);
  }, [refreshList]);

  const [context, setContext] = useState<HandoffContextResponse | null>(null);
  const [actionBusy, setActionBusy] = useState<string | null>(null);
  const [actionResult, setActionResult] = useState<any>(null);
  const [resourceModal, setResourceModal] = useState<SuggestedAction | null>(null);
  const [resourceDraft, setResourceDraft] = useState<string>('');

  useEffect(() => {
    if (!selected) { setDetail(null); setContext(null); setActionResult(null); return; }
    setLoading(true);
    setActionResult(null);
    authFetchJson<HandoffContextResponse>(
      `/api/handoffs/${selected}/context?organization_id=${organizationId}`,
    ).then(c => {
      setContext(c);
      setDetail(c.handoff);
    }).catch((e) => {
      console.warn('Failed to load handoff context, falling back to detail', e);
      // Fallback: just fetch the detail
      authFetchJson<HandoffDetail>(
        `/api/handoffs/${selected}?organization_id=${organizationId}`,
      ).then(setDetail).catch(() => setDetail(null));
      setContext(null);
    }).finally(() => setLoading(false));
  }, [selected, organizationId]);

  const runAction = async (
    action: SuggestedAction,
    extras: { message?: string; auto_resolve?: boolean } = {},
  ) => {
    if (!detail) return;
    setActionBusy(action.action_type);
    setActionResult(null);
    try {
      const res = await authFetch(
        `/api/handoffs/${detail.id}/take-action?organization_id=${organizationId}`,
        {
          method: 'POST',
          jsonBody: {
            action_type: action.action_type,
            user_id: currentUserId,
            message: extras.message ?? action.preview_message,
            auto_resolve: extras.auto_resolve ?? false,
          },
        },
      );
      if (!res.ok) {
        const txt = await res.text().catch(() => '');
        throw new Error(`HTTP ${res.status}: ${txt || res.statusText}`);
      }
      const j = await res.json();
      setActionResult(j);
      if (extras.auto_resolve) {
        setSelected(null);
        await refreshList();
      }
    } catch (e: any) {
      setActionResult({ ok: false, error: String(e?.message || e) });
    } finally {
      setActionBusy(null);
    }
  };

  const handleAssign = async () => {
    if (!detail) return;
    await authFetch(`/api/handoffs/${detail.id}/assign`, {
      method: 'POST', jsonBody: { user_id: currentUserId },
    });
    await refreshList();
  };

  const handleResolve = async (note: string) => {
    if (!detail) return;
    await authFetch(`/api/handoffs/${detail.id}/resolve`, {
      method: 'POST', jsonBody: { user_id: currentUserId, resolution_note: note },
    });
    setSelected(null);
    await refreshList();
  };

  const [createError, setCreateError] = useState<string | null>(null);

  const handleCreate = async () => {
    if (!createForm.member_id) return;
    setCreating(true); setCreateError(null);
    try {
      const res = await authFetch('/api/handoffs', {
        method: 'POST',
        jsonBody: {
          organization_id: organizationId,
          member_id: parseInt(createForm.member_id, 10),
          intent_label: createForm.intent_label || intentOptions[0]?.label || 'other',
          intent_confidence: 1.0,
          detected_language: createForm.detected_language || 'en',
          priority: createForm.priority,
          last_message_text: createForm.last_message_text || null,
          context_summary: createForm.context_summary || null,
        },
      });
      if (!res.ok) {
        const txt = await res.text().catch(() => '');
        throw new Error(`HTTP ${res.status}: ${txt || res.statusText}`);
      }
      setShowCreate(false);
      setCreateForm({
        member_id: '',
        intent_label: intentOptions[0]?.label || '',
        priority: (intentOptions[0]?.default_priority as any) || 'high',
        detected_language: 'en',
        last_message_text: '',
        context_summary: '',
      });
      await refreshList();
    } catch (e: any) {
      console.error('handoff create failed', e);
      setCreateError(String(e?.message || e) || 'Failed to create handoff');
    } finally { setCreating(false); }
  };

  return (
    <div className="flex h-[calc(100vh-100px)] flex-col gap-4">
      {/* Explainer banner — explains how items appear here without a chatflow */}
      <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900">
        <strong>How handoffs land here:</strong> any inbound message the
        Twilio webhook (and other communication-channel webhooks) receives
        from a member is routed through the platform's AI orchestration
        layer — auto-translated, classified against the org&apos;s{' '}
        <span className="font-mono">intent_taxonomy</span>, and audit-logged.
        If the matched intent has{' '}
        <span className="font-mono">auto_handoff=true</span> (e.g.{' '}
        <em>renewal_help</em>, <em>address_change</em>, <em>complaint</em>),
        OR the orchestration layer marks{' '}
        <span className="font-mono">needs_human=true</span>, a handoff request
        is created — <strong>no chatflow session is required</strong>.
        Handoffs created from inside an active chatflow also appear here and
        flip <span className="font-mono">chatflow_sessions.human_takeover=true</span>.
        This list polls every 10s.
      </div>
      <div className="flex flex-1 gap-4 overflow-hidden">
      {/* LEFT RAIL */}
      <div className="w-96 flex-shrink-0 overflow-y-auto rounded-lg border border-gray-200 bg-white">
        <div className="sticky top-0 z-10 flex flex-col gap-2 border-b border-gray-200 bg-white px-4 py-3">
          <div className="flex items-center justify-between">
            <div>
              {!tabbed && <h2 className="text-base font-semibold text-gray-900">CSR Inbox</h2>}

              <p className="text-xs text-gray-500">{handoffs.length} open handoff{handoffs.length === 1 ? '' : 's'}</p>
            </div>
            <button onClick={() => setShowCreate(true)}
              className="rounded bg-blue-600 px-2 py-1 text-xs text-white hover:bg-blue-700">
              + New handoff
            </button>
          </div>
          <SimulateInboundSMSButton
            organizationId={organizationId}
            onCreated={() => refreshList()} />
        </div>
        {prefilterMemberId && (
          <div className="border-b border-blue-200 bg-blue-50 px-4 py-2 text-xs text-blue-900 flex items-center justify-between">
            <span>
              Filtered to member <strong>#{prefilterMemberId}</strong> from inbox handoff link.
            </span>
            <button
              onClick={() => onOpenConversation?.(prefilterMemberId)}
              className="text-blue-700 underline hover:text-blue-900"
            >
              Clear
            </button>
          </div>
        )}
        {(() => {
          // Apply the optional member prefilter before rendering.
          const visible = prefilterMemberId
            ? handoffs.filter(h => h.member_id === prefilterMemberId)
            : handoffs;
          if (visible.length === 0) {
            return (
              <div className="px-4 py-8 text-center text-sm text-gray-500">
                {prefilterMemberId
                  ? `No open handoffs for member #${prefilterMemberId}.`
                  : 'No open handoffs. Inbound messages classified as needs_human will appear here.'}
              </div>
            );
          }
          return visible.map((h) => {
            const isSelected = h.id === selected;
            return (
              <div
                key={h.id}
                className={`block w-full border-b border-gray-100 transition ${
                  isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'
                }`}
              >
                <button
                  onClick={() => setSelected(h.id)}
                  className="block w-full px-4 py-3 text-left"
                >
                  <div className="flex items-center gap-2">
                    <span className={`h-2.5 w-2.5 rounded-full ${priorityColor(h.priority)}`} />
                    <span className="text-sm font-medium text-gray-900">
                      {h.member_first_name || 'Member'} {h.member_last_name || `#${h.member_id}`}
                    </span>
                    <span className="ml-auto text-base">
                      {LANG_FLAG[(h.detected_language || 'en').toLowerCase()] || '🌐'}
                    </span>
                  </div>
                  <div className="mt-1 text-xs font-medium text-blue-600">{h.intent_label}</div>
                  <div className="mt-1 line-clamp-2 text-xs text-gray-600">{h.last_message_text}</div>
                  <div className="mt-1 text-[10px] uppercase tracking-wide text-gray-400">
                    {h.priority} · {h.status}
                  </div>
                </button>
                {onOpenConversation && (
                  <div className="border-t border-gray-100 px-4 py-1.5">
                    <button
                      onClick={() => onOpenConversation(h.member_id)}
                      className="text-[11px] font-semibold text-blue-700 hover:text-blue-900"
                    >
                      ↗ Open conversation in 2-Way Inbox
                    </button>
                  </div>
                )}
              </div>
            );
          });
        })()}

        {/* Needs-triage lane — un-replied inbound that produced no handoff, so
            nothing slips past CSR. Hidden when scoped to a single member. */}
        {!prefilterMemberId && unrouted.length > 0 && (
          <div className="border-t-2 border-amber-200">
            <div className="bg-amber-50 px-4 py-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-amber-800">
                Needs triage · {unrouted.length}
              </p>
              <p className="text-[11px] text-amber-700">Inbound with no handoff yet — claim &amp; reply.</p>
            </div>
            {unrouted.map((u) => (
              <div key={u.conversation_id} className="block w-full border-b border-gray-100 hover:bg-amber-50/40">
                <div className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <span className="h-2.5 w-2.5 rounded-full bg-amber-400" />
                    <span className="text-sm font-medium text-gray-900">
                      {u.member_first_name || 'Member'} {u.member_last_name || `#${u.member_id}`}
                    </span>
                    <span className="ml-auto text-base">
                      {LANG_FLAG[(u.detected_language || 'en').toLowerCase()] || '🌐'}
                    </span>
                  </div>
                  <div className="mt-1 text-xs font-medium text-amber-700">
                    {u.intent_label || 'Unclassified'} · {u.channel}
                  </div>
                  <div className="mt-1 line-clamp-2 text-xs text-gray-600">{u.last_message_text}</div>
                </div>
                {onOpenConversation && (
                  <div className="border-t border-gray-100 px-4 py-1.5">
                    <button
                      onClick={() => onOpenConversation(u.member_id, u.channel)}
                      className="text-[11px] font-semibold text-amber-700 hover:text-amber-900"
                    >
                      ↗ Open &amp; respond in 2-Way Inbox
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* RIGHT PANE */}
      <div className="flex-1 overflow-y-auto rounded-lg border border-gray-200 bg-white">
        {!detail && !loading && (
          <div className="flex h-full items-center justify-center text-sm text-gray-500">
            Select a handoff to see member context.
          </div>
        )}
        {loading && (
          <div className="flex h-full items-center justify-center text-sm text-gray-500">Loading…</div>
        )}
        {detail && (
          <div className="flex h-full flex-col">
            {/* Header */}
            <div className="flex items-center gap-3 border-b border-gray-200 px-6 py-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-purple-500 text-white">
                {(detail.member?.first_name || '?')[0]}
              </div>
              <div>
                <div className="text-base font-semibold text-gray-900">
                  {detail.member?.first_name} {detail.member?.last_name}
                </div>
                <div className="text-xs text-gray-500">
                  {detail.member?.phone || 'no phone'} · preferred lang: {detail.member?.preferred_language || '—'}
                </div>
              </div>
              <span className="ml-auto text-2xl">
                {LANG_FLAG[(detail.detected_language || 'en').toLowerCase()] || '🌐'}
              </span>
            </div>

            {/* Intent badge + summary */}
            <div className="border-b border-gray-200 bg-blue-50 px-6 py-3">
              <div className="flex items-center gap-3">
                <span className="rounded-full bg-blue-600 px-2 py-0.5 text-xs font-bold uppercase text-white">
                  {detail.intent_label}
                </span>
                <span className="text-xs text-gray-600">
                  {detail.intent_confidence != null
                    ? `${(detail.intent_confidence * 100).toFixed(0)}% confidence`
                    : ''}
                </span>
                <CreateReferralFromIntentButton
                  cboId={organizationId}
                  classificationId={detail.intent_classification_id}
                  intentLabel={detail.intent_label}
                />
                <span className="ml-auto rounded-full bg-orange-500 px-2 py-0.5 text-[10px] font-bold uppercase text-white">
                  {detail.priority}
                </span>
              </div>
              <p className="mt-2 text-sm text-gray-700">{detail.context_summary}</p>
            </div>

            {/* Original + English message */}
            <div className="space-y-3 px-6 py-4">
              <div>
                <div className="text-[11px] font-bold uppercase tracking-wider text-gray-400">
                  Original ({detail.detected_language})
                </div>
                <div className="mt-1 rounded-lg bg-gray-100 px-3 py-2 text-sm text-gray-900">
                  {detail.original_text || detail.last_message_text || '—'}
                </div>
              </div>
              {detail.english_text && detail.detected_language !== 'en' && (
                <div>
                  <div className="text-[11px] font-bold uppercase tracking-wider text-gray-400">English translation</div>
                  <div className="mt-1 rounded-lg bg-gray-50 px-3 py-2 text-sm text-gray-700">
                    {detail.english_text}
                  </div>
                </div>
              )}
            </div>

            {/* Member context: profile + recent activity + consents */}
            {context?.member && (
              <div className="border-t border-gray-200 px-6 py-4">
                <div className="text-[11px] font-bold uppercase tracking-wider text-gray-400">
                  Member context
                </div>
                <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div className="rounded-lg border border-gray-100 bg-white p-3 text-sm">
                    <div className="mb-1 text-[10px] font-bold uppercase text-gray-500">Profile</div>
                    <div className="font-medium text-gray-900">
                      {context.member.first_name} {context.member.last_name} <span className="text-xs text-gray-500">· #{context.member.id}</span>
                    </div>
                    <div className="text-xs text-gray-600">
                      {context.member.phone || 'no phone'}
                      {context.member.email && ` · ${context.member.email}`}
                    </div>
                    {(context.member.city || context.member.state) && (
                      <div className="text-xs text-gray-500">
                        {context.member.city}{context.member.city && context.member.state ? ', ' : ''}{context.member.state} {context.member.zip_code || ''}
                      </div>
                    )}
                    <div className="mt-1 text-xs">
                      <span className="rounded bg-purple-100 px-2 py-0.5 font-mono uppercase text-purple-700">
                        {context.member.preferred_language || 'en'}
                      </span>
                      {context.member.date_of_birth && (
                        <span className="ml-2 text-gray-500">DOB {context.member.date_of_birth}</span>
                      )}
                    </div>
                  </div>
                  <div className="rounded-lg border border-gray-100 bg-white p-3 text-sm">
                    <div className="mb-1 text-[10px] font-bold uppercase text-gray-500">
                      Recent activity (30d)
                    </div>
                    {context.recent_events.length === 0 && (
                      <div className="text-xs text-gray-500">No engagement events on file.</div>
                    )}
                    {context.recent_events.slice(0, 5).map((ev, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs">
                        <span className={`rounded px-1.5 py-0.5 text-[9px] font-bold uppercase ${
                          ev.event_type === 'delivered' ? 'bg-green-100 text-green-700' :
                          ev.event_type === 'responded' ? 'bg-blue-100 text-blue-700' :
                          ev.event_type === 'opted_out' ? 'bg-amber-100 text-amber-900' :
                          ev.event_type === 'bounced' ? 'bg-red-100 text-red-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {ev.event_type}
                        </span>
                        <span className="text-gray-500">{ev.channel || ''}</span>
                        <span className="truncate text-gray-700">{ev.campaign_name || '—'}</span>
                        <span className="ml-auto whitespace-nowrap text-gray-400">
                          {ev.occurred_at ? new Date(ev.occurred_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : ''}
                        </span>
                      </div>
                    ))}
                    {context.recent_events.length > 5 && (
                      <div className="mt-1 text-[10px] text-gray-500">
                        +{context.recent_events.length - 5} more — open Reports → Campaign Progress for full timeline
                      </div>
                    )}
                  </div>
                </div>
                {context.consents && context.consents.length > 0 && (
                  <div className="mt-3 rounded-lg border border-gray-100 bg-white p-3 text-xs">
                    <div className="mb-1 text-[10px] font-bold uppercase text-gray-500">Consents</div>
                    <div className="flex flex-wrap gap-1">
                      {context.consents.map(c => (
                        <span key={c.category_key}
                          className={`rounded px-2 py-0.5 ${c.granted ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500 line-through'}`}>
                          {c.category_key}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Proposed actions */}
            {context?.suggested_actions && context.suggested_actions.length > 0 && (
              <div className="border-t border-gray-200 bg-amber-50/40 px-6 py-4">
                <div className="mb-2 flex items-center justify-between">
                  <div className="text-[11px] font-bold uppercase tracking-wider text-gray-500">
                    Proposed course of action
                  </div>
                  <span className="text-[10px] text-gray-500">
                    Suggested by intent <span className="font-mono">{detail.intent_label}</span>
                  </span>
                </div>
                <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
                  {context.suggested_actions.map(action => {
                    const isBusy = actionBusy === action.action_type;
                    const isPrimary = action.primary;
                    const onClick = () => {
                      if (action.action_type === 'send_resources') {
                        setResourceModal(action);
                        setResourceDraft(action.preview_message || '');
                      } else {
                        runAction(action);
                      }
                    };
                    return (
                      <button key={action.action_type}
                        onClick={onClick} disabled={isBusy}
                        className={`flex flex-col items-start gap-1 rounded-lg border p-3 text-left transition disabled:opacity-50 ${
                          isPrimary ? 'border-blue-300 bg-blue-50 hover:bg-blue-100' :
                          'border-gray-200 bg-white hover:bg-gray-50'
                        }`}>
                        <div className="flex w-full items-center gap-2">
                          <span className="text-base">
                            {action.icon === 'ticket' ? '🎫' :
                             action.icon === 'headset' ? '🎧' : '✉️'}
                          </span>
                          <span className="text-sm font-semibold text-gray-900">{action.label}</span>
                          {isPrimary && (
                            <span className="ml-auto rounded-full bg-blue-600 px-2 py-0.5 text-[9px] font-bold uppercase text-white">
                              Recommended
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-gray-600">{action.description}</div>
                        <div className="mt-1 flex items-center gap-2 text-[10px] uppercase text-gray-500">
                          <span>System: {action.system}</span>
                          {isBusy && <span>working…</span>}
                        </div>
                      </button>
                    );
                  })}
                </div>
                {actionResult && actionResult.ok && (
                  <div className="mt-3 rounded-lg border border-green-200 bg-green-50 p-3 text-xs text-green-900">
                    ✓ <strong>{(actionResult.action_type === 'crm_ticket' || actionResult.action_type === 'uuhp_ticket') ? 'Payer CRM ticket' :
                                actionResult.action_type === 'weavein_ticket' ? 'Weave In CSM ticket' :
                                'Resource SMS'}</strong> created · <span className="font-mono">
                      {actionResult.confirmation?.ticket_number || actionResult.confirmation?.message_sid}
                    </span>
                    {actionResult.confirmation?.queue && <span> · queue: {actionResult.confirmation.queue}</span>}
                    {actionResult.confirmation?.priority && <span> · priority: {actionResult.confirmation.priority}</span>}
                    {actionResult.auto_resolved && <span> · handoff resolved</span>}
                  </div>
                )}
                {actionResult && !actionResult.ok && actionResult.error && (
                  <div className="mt-3 rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-800">
                    {actionResult.error}
                  </div>
                )}
              </div>
            )}

            {/* Conversation history */}
            {detail.conversation_history && detail.conversation_history.length > 0 && (
              <div className="border-t border-gray-200 px-6 py-4">
                <div className="text-[11px] font-bold uppercase tracking-wider text-gray-400">
                  Conversation history ({detail.conversation_history.length} turn{detail.conversation_history.length === 1 ? '' : 's'})
                </div>
                <div className="mt-2 space-y-2">
                  {detail.conversation_history.slice(-5).map((m, i) => (
                    <div key={i} className={`text-sm ${m.role === 'member' ? 'text-blue-700' : 'text-gray-700'}`}>
                      <span className="font-semibold">{m.role || 'system'}:</span> {m.content}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="mt-auto flex items-center gap-2 border-t border-gray-200 bg-gray-50 px-6 py-3">
              {detail.assigned_to_user_id == null && (
                <button
                  onClick={handleAssign}
                  className="rounded bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700"
                >
                  Take ownership
                </button>
              )}
              <button
                onClick={() => handleResolve('Resolved by CSR')}
                className="rounded bg-green-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-700"
              >
                Mark resolved
              </button>
              <span className="ml-auto text-xs text-gray-500">
                Created {detail.created_at ? new Date(detail.created_at).toLocaleString() : ''}
              </span>
            </div>
          </div>
        )}
      </div>
      </div>

      {/* Send resources modal — preview + edit + send */}
      {resourceModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-lg rounded-lg bg-white p-5">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-base font-semibold">Send follow-up with resources</h2>
              <button onClick={() => { setResourceModal(null); setResourceDraft(''); }}
                className="text-gray-500 hover:text-gray-900">✕</button>
            </div>
            <p className="mb-3 text-xs text-gray-500">
              Pre-rendered in <span className="font-mono">{resourceModal.preview_language || 'en'}</span> for this member's
              preferred language. Edit if needed before sending. The
              outbound SMS is logged to the engagement-events stream so it
              shows up on Sofia's timeline.
            </p>
            <textarea
              value={resourceDraft}
              onChange={e => setResourceDraft(e.target.value)}
              rows={8}
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm" />
            <label className="mt-2 flex items-center gap-2 text-xs text-gray-700">
              <input type="checkbox" defaultChecked
                id="csr-auto-resolve"
                onChange={() => {}} />
              Mark this handoff resolved after sending
            </label>
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => { setResourceModal(null); setResourceDraft(''); }}
                className="rounded bg-gray-100 px-3 py-1.5 text-sm">Cancel</button>
              <button
                onClick={async () => {
                  const auto = (document.getElementById('csr-auto-resolve') as HTMLInputElement | null)?.checked ?? true;
                  await runAction(resourceModal!, { message: resourceDraft, auto_resolve: auto });
                  setResourceModal(null);
                  setResourceDraft('');
                }}
                disabled={!resourceDraft.trim() || actionBusy === 'send_resources'}
                className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700 disabled:opacity-50">
                {actionBusy === 'send_resources' ? 'Sending…' : 'Send SMS'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Manual handoff creation modal */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-lg bg-white p-5">
            <h2 className="mb-3 text-base font-semibold">Create handoff request</h2>
            <p className="mb-3 text-xs text-gray-500">
              Pulls a member into the CSR queue without waiting for an inbound
              SMS — useful when escalating from a phone call, voicemail, or a
              chatflow that doesn&apos;t have an explicit handoff node.
            </p>
            <div className="space-y-2">
              <div>
                <label className="text-xs font-bold uppercase text-gray-500">Member</label>
                <select value={createForm.member_id}
                  onChange={e => {
                    const id = e.target.value;
                    const m = memberOptions.find(o => String(o.id) === id);
                    const lang = m?.preferred_language || m?.language || 'en';
                    const langMap: Record<string, string> = {
                      English: 'en', Spanish: 'es', Vietnamese: 'vi', Chinese: 'zh',
                      Arabic: 'ar', Korean: 'ko',
                    };
                    setCreateForm({
                      ...createForm,
                      member_id: id,
                      detected_language: langMap[lang] || (lang || 'en').toLowerCase().slice(0, 2),
                    });
                  }}
                  className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm">
                  <option value="">— Select a member —</option>
                  {memberOptions.map(m => (
                    <option key={m.id} value={m.id}>
                      {(m.first_name || '') + ' ' + (m.last_name || '')} (ID {m.id})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs font-bold uppercase text-gray-500">Intent</label>
                <select value={createForm.intent_label}
                  onChange={e => {
                    const label = e.target.value;
                    const it = intentOptions.find(i => i.label === label);
                    setCreateForm({
                      ...createForm,
                      intent_label: label,
                      // honor the taxonomy default priority for the chosen intent
                      priority: (it?.default_priority as any) || createForm.priority,
                    });
                  }}
                  className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm">
                  {intentOptions.length === 0 && (
                    <option value="">(loading intent taxonomy…)</option>
                  )}
                  {intentOptions.map(it => (
                    <option key={it.id} value={it.label}>
                      {it.label} {it.auto_handoff ? ' · auto-handoff' : ' · manual'}
                    </option>
                  ))}
                </select>
                {selectedIntent?.description && (
                  <p className="mt-1 text-xs text-gray-500">{selectedIntent.description}</p>
                )}
                {selectedIntent && (
                  <p className="mt-0.5 text-[11px] text-gray-400">
                    Default priority for this intent: <span className="font-mono">{selectedIntent.default_priority}</span>
                    {selectedIntent.auto_handoff
                      ? ' · would auto-create a handoff on real inbound SMS'
                      : ' · only escalates when the intent classifier marks needs_human=true'}
                  </p>
                )}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <select value={createForm.priority}
                  onChange={e => setCreateForm({ ...createForm, priority: e.target.value as any })}
                  className="rounded border border-gray-300 px-3 py-2 text-sm">
                  <option value="urgent">Urgent</option>
                  <option value="high">High</option>
                  <option value="normal">Normal</option>
                  <option value="low">Low</option>
                </select>
                <select value={createForm.detected_language}
                  onChange={e => setCreateForm({ ...createForm, detected_language: e.target.value })}
                  className="rounded border border-gray-300 px-3 py-2 text-sm">
                  <option value="en">English</option>
                  <option value="es">Spanish</option>
                  <option value="vi">Vietnamese</option>
                  <option value="zh">Chinese</option>
                  <option value="ar">Arabic</option>
                  <option value="ko">Korean</option>
                </select>
              </div>
              <textarea value={createForm.last_message_text}
                onChange={e => setCreateForm({ ...createForm, last_message_text: e.target.value })}
                rows={2}
                placeholder="Last message from member (optional)"
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm" />
              <textarea value={createForm.context_summary}
                onChange={e => setCreateForm({ ...createForm, context_summary: e.target.value })}
                rows={2}
                placeholder="Context summary (1-2 sentences)"
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm" />
            </div>
            {createError && (
              <div className="mt-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
                {createError}
              </div>
            )}
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => { setShowCreate(false); setCreateError(null); }}
                className="rounded bg-gray-100 px-3 py-1.5 text-sm">Cancel</button>
              <button onClick={handleCreate} disabled={creating || !createForm.member_id}
                className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700 disabled:opacity-50">
                {creating ? 'Creating…' : 'Create handoff'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CSRInboxPage;
